import { useState } from 'react';
import { Database, RotateCcw, Save } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, Card, CardTitle, IconBox } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import {
  MAINTENANCE_LABEL,
  type MaintenanceAction,
  type MaintenanceResponse,
} from '../schemas';
import { useMaintenance } from '../api';

const ACTION_ICON: Record<MaintenanceAction, LucideIcon> = {
  flush_cache: Database,
  reset_embeddings: RotateCcw,
  backup: Save,
};

export function MaintenancePanel() {
  const maint = useMaintenance();
  const [lastAction, setLastAction] = useState<MaintenanceAction | null>(null);
  const [lastResult, setLastResult] = useState<MaintenanceResponse | null>(null);

  function run(action: MaintenanceAction) {
    if (!confirm(`Executar "${MAINTENANCE_LABEL[action].title}"?`)) return;
    setLastAction(action);
    maint.mutate(action, {
      onSuccess: (res) => setLastResult(res),
      onError: () => setLastResult(null),
    });
  }

  return (
    <Card padding="lg">
      <Stack gap="md">
        <CardTitle>Manutenção</CardTitle>

        <ul className="flex flex-col divide-y divide-border">
          {(Object.keys(MAINTENANCE_LABEL) as MaintenanceAction[]).map((action) => {
            const Icon = ACTION_ICON[action];
            const meta = MAINTENANCE_LABEL[action];
            const isRunning = maint.isPending && lastAction === action;
            return (
              <li key={action} className="py-3 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <IconBox tone="neutral" size="md">
                    <Icon size={14} strokeWidth={1.5} />
                  </IconBox>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-text">{meta.title}</span>
                    <span className="text-xs text-text-2">{meta.desc}</span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={maint.isPending}
                  onClick={() => run(action)}
                >
                  {isRunning ? 'Executando…' : 'Executar'}
                </Button>
              </li>
            );
          })}
        </ul>

        {maint.isSuccess && lastAction && lastResult && (
          <p className="text-xs text-ok">
            {MAINTENANCE_LABEL[lastAction].title} concluída.
            {lastResult.deleted !== undefined && ` ${lastResult.deleted} chaves removidas.`}
            {lastResult.reset !== undefined && ` ${lastResult.reset} itens resetados.`}
          </p>
        )}
        {maint.error && (
          <p className="text-xs text-danger">
            {maint.error instanceof ApiError ? maint.error.message : 'Falha na operação'}
          </p>
        )}
      </Stack>
    </Card>
  );
}
